// src/sql.ts
function raw(text) {
  return { kind: "raw", text };
}
function param(value) {
  return { kind: "param", value };
}
function dialectNode(resolve) {
  return { kind: "dialect", resolve };
}
function isSqlNode(value) {
  return typeof value === "object" && value !== null && "kind" in value;
}
function sql(strings, ...values) {
  const nodes = [];
  strings.forEach((text, index) => {
    if (text.length > 0) nodes.push({ kind: "text", text });
    if (index < values.length) {
      const value = values[index];
      nodes.push(isSqlNode(value) ? value : param(value));
    }
  });
  return { kind: "frag", nodes };
}
function join(nodes, separator) {
  const out = [];
  nodes.forEach((node, index) => {
    if (index > 0) out.push({ kind: "text", text: separator });
    out.push(node);
  });
  return { kind: "frag", nodes: out };
}
function render(node, dialect, sink) {
  function walk(current) {
    switch (current.kind) {
      case "text":
      case "raw":
        sink.text(current.text);
        return;
      case "param":
        sink.param(current.value);
        return;
      case "frag":
        for (const child of current.nodes) walk(child);
        return;
      case "dialect":
        walk(current.resolve(dialect));
        return;
    }
  }
  walk(node);
  return sink.finish();
}
function positionalSink() {
  let text = "";
  const params = [];
  return {
    text(chunk) {
      text += chunk;
    },
    param(value) {
      text += "?";
      params.push(typeof value === "boolean" ? value ? 1 : 0 : value);
    },
    finish() {
      return { sql: text, params };
    }
  };
}
function renderSqlite(node, dialect) {
  return render(node, dialect, positionalSink());
}

// src/dialect.ts
var postgresDialect = {
  name: "postgres",
  castText(expr) {
    return sql`${expr}::text`;
  },
  caseInsensitiveLike(column, pattern) {
    return sql`${column} ILIKE ${pattern}`;
  },
  orderByClause(column, direction) {
    return sql`${column} ${raw(direction === "asc" ? "ASC" : "DESC")}`;
  },
  jsonArrayLength(expr) {
    return sql`jsonb_array_length(${expr})`;
  },
  castInt(expr) {
    return sql`(${expr})::int`;
  },
  castNumeric(expr) {
    return sql`(${expr})::numeric`;
  },
  jsonEachText(expr, alias) {
    return sql`jsonb_each_text(${expr}) AS ${raw(alias)}(key, value)`;
  },
  jsonValueIsTrue(value) {
    return sql`${value} = 'true'`;
  },
  substringBefore(expr, delimiter) {
    return sql`(CASE WHEN strpos(${expr}, ${param(delimiter)}) > 0 THEN substr(${expr}, 1, strpos(${expr}, ${param(delimiter)}) - 1) ELSE ${expr} END)`;
  },
  substringAfter(expr, delimiter) {
    return sql`(CASE WHEN strpos(${expr}, ${param(delimiter)}) > 0 THEN substr(${expr}, strpos(${expr}, ${param(delimiter)}) + 1) ELSE NULL END)`;
  }
};
var sqliteDialect = {
  name: "sqlite",
  castText(expr) {
    return sql`CAST(${expr} AS TEXT)`;
  },
  caseInsensitiveLike(column, pattern) {
    return sql`${column} LIKE ${pattern} ESCAPE '\\'`;
  },
  orderByClause(column, direction, type) {
    const collate = type === "text" ? raw(" COLLATE NOCASE") : raw("");
    const nulls = raw(direction === "asc" ? "NULLS LAST" : "NULLS FIRST");
    return sql`${column}${collate} ${raw(direction === "asc" ? "ASC" : "DESC")} ${nulls}`;
  },
  jsonArrayLength(expr) {
    return sql`json_array_length(${expr})`;
  },
  castInt(expr) {
    return sql`CAST(${expr} AS INTEGER)`;
  },
  castNumeric(expr) {
    return sql`CAST(${expr} AS REAL)`;
  },
  jsonEachText(expr, alias) {
    return sql`json_each(${expr}) AS ${raw(alias)}`;
  },
  jsonValueIsTrue(value) {
    return sql`${value} IS 1`;
  },
  substringBefore(expr, delimiter) {
    return sql`(CASE WHEN instr(${expr}, ${param(delimiter)}) > 0 THEN substr(${expr}, 1, instr(${expr}, ${param(delimiter)}) - 1) ELSE ${expr} END)`;
  },
  substringAfter(expr, delimiter) {
    return sql`(CASE WHEN instr(${expr}, ${param(delimiter)}) > 0 THEN substr(${expr}, instr(${expr}, ${param(delimiter)}) + 1) ELSE NULL END)`;
  }
};

// src/coerce.ts
var NUMERIC_RE = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;
function coerce(value, type) {
  if (type === "int") {
    if (typeof value === "number") return value;
    if (typeof value === "string" && /^-?\d+$/.test(value)) return Number(value);
    throw new Error(`Invalid int value: ${JSON.stringify(value)}`);
  }
  if (type === "numeric") {
    if (typeof value === "number") return value;
    if (typeof value === "string" && NUMERIC_RE.test(value)) return Number(value);
    if (value && typeof value === "object" && "toString" in value) {
      const str = value.toString();
      if (NUMERIC_RE.test(str)) return Number(str);
    }
    throw new Error(`Invalid numeric value: ${JSON.stringify(value)}`);
  }
  if (type === "bool") {
    if (typeof value === "boolean") return value;
    throw new Error(`Invalid bool value: ${JSON.stringify(value)}`);
  }
  if (typeof value !== "string") {
    throw new Error(`Invalid text value: ${JSON.stringify(value)}`);
  }
  return value;
}

// src/filter.ts
var LIKE_SPECIAL = /[\\%_]/g;
function escapeLike(value) {
  return value.replace(LIKE_SPECIAL, (ch) => `\\${ch}`);
}
function createFilterBuilder(alias, columns) {
  const prefix = alias.length > 0 ? `${alias}.` : "";
  function columnSql(field) {
    const column = columns[field];
    if (!column) throw new Error(`Unsupported where/orderBy field: ${field}`);
    const base = raw(`${prefix}"${column.sqlName}"`);
    return column.cast === "text" ? dialectNode((dialect) => dialect.castText(base)) : base;
  }
  function columnType(field) {
    const column = columns[field];
    if (!column) throw new Error(`Unsupported where/orderBy field: ${field}`);
    return column.type;
  }
  function buildFieldPredicate(field, condition) {
    const column = columns[field];
    if (!column) throw new Error(`Unsupported where field: ${field}`);
    const colSql = columnSql(field);
    const parts = [];
    for (const [op, value] of Object.entries(condition)) {
      if (value === void 0) continue;
      switch (op) {
        case "equals":
          parts.push(
            value === null ? sql`${colSql} IS NULL` : sql`${colSql} = ${param(coerce(value, column.type))}`
          );
          break;
        case "not":
          if (value === null) {
            parts.push(sql`${colSql} IS NOT NULL`);
          } else if (typeof value === "object") {
            parts.push(sql`NOT (${buildFieldPredicate(field, value)})`);
          } else {
            parts.push(sql`${colSql} <> ${param(coerce(value, column.type))}`);
          }
          break;
        case "in": {
          if (!Array.isArray(value)) throw new Error(`'in' expects array for ${field}`);
          if (value.length === 0) {
            parts.push(raw("FALSE"));
          } else {
            const values = value.map((entry) => param(coerce(entry, column.type)));
            parts.push(sql`${colSql} IN (${join(values, ", ")})`);
          }
          break;
        }
        case "notIn": {
          if (!Array.isArray(value)) throw new Error(`'notIn' expects array for ${field}`);
          if (value.length === 0) {
            parts.push(raw("TRUE"));
          } else {
            const values = value.map((entry) => param(coerce(entry, column.type)));
            parts.push(sql`${colSql} NOT IN (${join(values, ", ")})`);
          }
          break;
        }
        case "gt":
          parts.push(sql`${colSql} > ${param(coerce(value, column.type))}`);
          break;
        case "gte":
          parts.push(sql`${colSql} >= ${param(coerce(value, column.type))}`);
          break;
        case "lt":
          parts.push(sql`${colSql} < ${param(coerce(value, column.type))}`);
          break;
        case "lte":
          parts.push(sql`${colSql} <= ${param(coerce(value, column.type))}`);
          break;
        case "contains": {
          if (typeof value !== "string") throw new Error(`'contains' expects string for ${field}`);
          const pattern = param(`%${escapeLike(value)}%`);
          parts.push(dialectNode((dialect) => dialect.caseInsensitiveLike(colSql, pattern)));
          break;
        }
        case "startsWith": {
          if (typeof value !== "string") throw new Error(`'startsWith' expects string for ${field}`);
          const pattern = param(`${escapeLike(value)}%`);
          parts.push(dialectNode((dialect) => dialect.caseInsensitiveLike(colSql, pattern)));
          break;
        }
        case "endsWith": {
          if (typeof value !== "string") throw new Error(`'endsWith' expects string for ${field}`);
          const pattern = param(`%${escapeLike(value)}`);
          parts.push(dialectNode((dialect) => dialect.caseInsensitiveLike(colSql, pattern)));
          break;
        }
        case "mode":
          break;
        default:
          throw new Error(`Unsupported where operator '${op}' on field '${field}'`);
      }
    }
    if (parts.length === 0) return raw("TRUE");
    if (parts.length === 1) return parts[0];
    return sql`(${join(parts, " AND ")})`;
  }
  function buildWhere(where) {
    const parts = [];
    for (const [key, value] of Object.entries(where)) {
      if (value === void 0 || value === null) continue;
      if (key === "AND" || key === "OR") {
        if (!Array.isArray(value)) throw new Error(`'${key}' must be an array`);
        if (value.length === 0) continue;
        const children = value.map((child) => buildWhere(child));
        parts.push(sql`(${join(children, key === "AND" ? " AND " : " OR ")})`);
        continue;
      }
      if (key === "NOT") {
        const list = Array.isArray(value) ? value : [value];
        if (list.length === 0) continue;
        const children = list.map((child) => buildWhere(child));
        parts.push(sql`NOT (${join(children, " AND ")})`);
        continue;
      }
      parts.push(buildFieldPredicate(key, value));
    }
    if (parts.length === 0) return raw("TRUE");
    return sql`(${join(parts, " AND ")})`;
  }
  function buildOrderBy(orderBy) {
    const entries = Array.isArray(orderBy) ? orderBy : [orderBy];
    const clauses = [];
    for (const entry of entries) {
      for (const [field, direction] of Object.entries(entry)) {
        if (direction !== "asc" && direction !== "desc") {
          throw new Error(`Invalid order direction for ${field}: ${direction}`);
        }
        const colSql = columnSql(field);
        const type = columnType(field);
        clauses.push(dialectNode((dialect) => dialect.orderByClause(colSql, direction, type)));
      }
    }
    clauses.push(raw(`${prefix}"id" ASC`));
    return join(clauses, ", ");
  }
  return { columnSql, buildWhere, buildOrderBy };
}

// src/filteredNovelties.ts
var isPg = (name) => name === "postgres";
function castText(expr) {
  return dialectNode((dialect) => dialect.castText(expr));
}
function castInt(expr) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`(${expr})::int` : sql`CAST(${expr} AS INTEGER)`
  );
}
function jsonText(jsonExpr, keyExpr) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`(${jsonExpr}) ->> (${keyExpr})` : sql`json_extract(${jsonExpr}, '$."' || (${keyExpr}) || '"')`
  );
}
function jsonNestedText(jsonExpr, keyExpr, member) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`(${jsonExpr}) -> (${keyExpr}) ->> ${param(member)}` : sql`json_extract(${jsonExpr}, '$."' || (${keyExpr}) || '"."${raw(member)}"')`
  );
}
function jsonTypeof(jsonExpr) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`jsonb_typeof(${jsonExpr})` : sql`json_type(${jsonExpr})`
  );
}
function jsonArrayLength(jsonExpr) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`jsonb_array_length(${jsonExpr})` : sql`json_array_length(${jsonExpr})`
  );
}
function notRead(readStatus, keyExpr) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`(${readStatus} ->> (${keyExpr})) IS DISTINCT FROM 'true'` : sql`json_extract(${readStatus}, '$."' || (${keyExpr}) || '"') IS NOT 1`
  );
}
function tableRef(name) {
  return raw(`"${name}"`);
}
function vkey(numberText) {
  return sql`${numberText} || COALESCE('-' || NULLIF(n."volumeSuffix", ''), '')`;
}
function buildModeSql(filterModeListType, releaseItems, numberText) {
  if (filterModeListType === "unowned") {
    return sql`(
      CASE ${castText(raw('n."relationType"'))}
        WHEN 'volume' THEN
          n."number" IS NULL
          OR COALESCE(${castInt(jsonText(raw('urs."itemStatus"'), vkey(numberText)))}, 0) <= 0
        WHEN 'collection' THEN
          COALESCE(${castInt(jsonNestedText(raw('urs."miscItemStatus"'), raw('n."relationId"'), "quantity"))}, 0) <= 0
        WHEN 'extra' THEN
          COALESCE(${castInt(jsonNestedText(raw('urs."miscItemStatus"'), raw('n."relationId"'), "quantity"))}, 0) <= 0
        ELSE TRUE
      END
    )`;
  }
  if (filterModeListType === "unread") {
    return sql`(
      CASE ${castText(raw('n."relationType"'))}
        WHEN 'volume' THEN
          n."number" IS NULL
          OR ${notRead(raw('latest_read."readStatus"'), numberText)}
        WHEN 'collection' THEN
          ${jsonTypeof(releaseItems)} <> 'array'
          OR ${jsonArrayLength(releaseItems)} = 0
          OR ${existsUnreadCollectionVolume(releaseItems)}
        ELSE FALSE
      END
    )`;
  }
  return raw("TRUE");
}
function existsUnreadCollectionVolume(releaseItems) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`EXISTS (
          SELECT 1
          FROM jsonb_array_elements_text(${releaseItems}) AS e(volume_number)
          WHERE (latest_read."readStatus" ->> e.volume_number) IS DISTINCT FROM 'true'
        )` : sql`EXISTS (
          SELECT 1
          FROM json_each(${releaseItems}) AS e
          WHERE json_extract(latest_read."readStatus", '$."' || e.value || '"') IS NOT 1
        )`
  );
}
function buildAltVersionSql(tables, numberText, releaseItemNumberText) {
  const altVolumeOtherOwned = dialectNode(
    (dialect) => isPg(dialect.name) ? sql`EXISTS (
          SELECT 1
          FROM jsonb_each_text(urs."itemStatus") AS kv(key, value)
          WHERE kv.key LIKE ${numberText} || '%'
            AND COALESCE(kv.value::int, 0) > 0
        )` : sql`EXISTS (
          SELECT 1
          FROM json_each(urs."itemStatus") AS kv
          WHERE kv.key LIKE ${numberText} || '%'
            AND COALESCE(CAST(kv.value AS INTEGER), 0) > 0
        )`
  );
  const altVolumeThisOwned = sql`COALESCE(${castInt(jsonText(raw('urs."itemStatus"'), vkey(numberText)))}, 0) > 0`;
  const altMiscSameNumberOwned = sql`EXISTS (
    SELECT 1
    FROM ${tableRef(tables.releaseItemCollection)} ric
    WHERE ric."entryId" = n."entryId"
      AND ric."number" = n."number"
      AND COALESCE(${castInt(jsonNestedText(raw('urs."miscItemStatus"'), raw('ric."id"'), "quantity"))}, 0) > 0
  )`;
  const altMiscReleaseItemOwned = sql`EXISTS (
    SELECT 1
    FROM ${tableRef(tables.releaseItemCollectionLink)} link
    JOIN ${tableRef(tables.releaseItem)} ri ON ri."id" = link."A"
    WHERE link."B" = n."relationId"
      AND COALESCE(${castInt(jsonText(raw('urs."itemStatus"'), releaseItemNumberText))}, 0) > 0
  )`;
  const altMiscThisOwned = sql`COALESCE(${castInt(jsonNestedText(raw('urs."miscItemStatus"'), raw('n."relationId"'), "quantity"))}, 0) > 0`;
  return sql`(
    CASE ${castText(raw('n."relationType"'))}
      WHEN 'volume' THEN
        n."number" IS NULL
        OR (NOT ${altVolumeOtherOwned}) OR ${altVolumeThisOwned}
      WHEN 'collection' THEN
        n."number" IS NULL
        OR (NOT (${altMiscSameNumberOwned} OR ${altMiscReleaseItemOwned}))
        OR ${altMiscThisOwned}
      WHEN 'extra' THEN
        n."number" IS NULL
        OR (NOT (${altMiscSameNumberOwned} OR ${altMiscReleaseItemOwned}))
        OR ${altMiscThisOwned}
      ELSE TRUE
    END
  )`;
}
function extractTargetMonth(where) {
  const year = where.releaseYear?.equals;
  const month = where.releaseMonth?.equals;
  if (!year || !month) return void 0;
  return { year, month };
}
function buildTargetMonthSql(targetMonth) {
  if (!targetMonth || !targetMonth.year || !targetMonth.month) return raw("TRUE");
  const { year, month } = targetMonth;
  const mm = String(month).padStart(2, "0");
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const startStr = `${year}-${mm}-01`;
  const endStr = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;
  const monthEnd = dateLiteral(endStr);
  const monthStart = dateLiteral(startStr);
  return sql`(urs."startDate" IS NULL OR urs."startDate" <= ${monthEnd})
    AND (urs."endDate" IS NULL OR urs."endDate" >= ${monthStart})`;
}
function dateLiteral(value) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`${param(value)}::date` : sql`${param(value)}`
  );
}
var BASE_EXCLUDED_STATUSES = ["sold", "aborted"];
var NOT_IN_COLLECTION_MODE = "notInCollection";
function statusList(statuses) {
  return join(
    statuses.map((status) => param(status)),
    ", "
  );
}
function notInCollectionSql() {
  return sql`(
    urs."id" IS NULL
    OR ${castText(raw('urs."collectionStatus"'))} IN (${statusList(BASE_EXCLUDED_STATUSES)})
  )`;
}
function excludedStatusList(filterModeParts) {
  const statuses = [...BASE_EXCLUDED_STATUSES];
  if (filterModeParts.includes("hidePlanned")) statuses.push("planned");
  if (filterModeParts.includes("hideCollecting")) statuses.push("collecting");
  if (filterModeParts.includes("hidePaused")) statuses.push("paused");
  return statusList(statuses);
}
function entryStatusJoin(tables, entryUserMatch, optional) {
  return sql`${raw(optional ? "LEFT JOIN" : "INNER JOIN")} ${tableRef(tables.userReleaseEntryStatus)} urs
      ON urs."entry" = n."entryId"
     AND ${entryUserMatch}`;
}
function limitSql(take) {
  if (take === void 0 || take === null) {
    return dialectNode((dialect) => isPg(dialect.name) ? raw("ALL") : raw("-1"));
  }
  return param(take);
}
function buildFilteredNoveltyIdsSql(args) {
  const { where, orderBy, filterMode, take, skip, targetMonth, tables, releaseItems } = args;
  const { noveltyNumberText, releaseItemNumberText } = args;
  const filterModeParts = filterMode.split("|");
  const filterModeListType = filterModeParts[0];
  const notInCollection = filterModeListType === NOT_IN_COLLECTION_MODE;
  const doNotShowAlternativesWhenOwned = filterModeParts.includes("doNotShowAlternativesWhenOwned");
  const modeSql2 = notInCollection ? raw("TRUE") : buildModeSql(filterModeListType, releaseItems, noveltyNumberText);
  const altSql = doNotShowAlternativesWhenOwned && !notInCollection ? buildAltVersionSql(tables, noveltyNumberText, releaseItemNumberText) : raw("TRUE");
  const collectingWindowSql = notInCollection ? raw("TRUE") : buildTargetMonthSql(targetMonth);
  const membershipSql = notInCollection ? notInCollectionSql() : sql`${castText(raw('urs."collectionStatus"'))} NOT IN (${excludedStatusList(filterModeParts)})`;
  const passingNovelties = sql`
    SELECT ${rankedNoveltyColumns(tables)}
    FROM ${tableRef(tables.novelty)} n
    ${entryStatusJoin(tables, args.entryUserMatch, notInCollection)}
    ${latestReadJoin(tables)}
    WHERE ${where}
      AND ${membershipSql}
      AND ${collectingWindowSql}
      AND ${modeSql2}
      AND ${altSql}
  `;
  return oneNoveltyPerBox({ passingNovelties, tables, orderBy, take, skip });
}
function buildCatalogueNoveltyIdsSql(args) {
  const { where, orderBy, take, skip, tables } = args;
  const passingNovelties = sql`
    SELECT ${rankedNoveltyColumns(tables)}
    FROM ${tableRef(tables.novelty)} n
    WHERE ${where}
  `;
  return oneNoveltyPerBox({ passingNovelties, tables, orderBy, take, skip });
}
function buildFilteredCustomListNoveltyIdsSql(args) {
  const { listId, where, orderBy, filterMode, take, skip, targetMonth, tables, releaseItems } = args;
  const { noveltyNumberText, releaseItemNumberText } = args;
  const filterModeParts = (filterMode ?? "").split("|");
  const hasFilterMode = filterMode !== null && filterMode !== "";
  const notInCollection = filterModeParts[0] === NOT_IN_COLLECTION_MODE;
  const doNotShowAlternativesWhenOwned = filterModeParts.includes("doNotShowAlternativesWhenOwned");
  const modeSql2 = hasFilterMode && !notInCollection ? buildModeSql(filterModeParts[0], releaseItems, noveltyNumberText) : raw("TRUE");
  const altSql = hasFilterMode && doNotShowAlternativesWhenOwned && !notInCollection ? buildAltVersionSql(tables, noveltyNumberText, releaseItemNumberText) : raw("TRUE");
  const collectingWindowSql = notInCollection ? raw("TRUE") : buildTargetMonthSql(targetMonth);
  const excludedSql = notInCollection ? notInCollectionSql() : hasFilterMode ? sql`(urs."collectionStatus" IS NULL
        OR ${castText(raw('urs."collectionStatus"'))} NOT IN (${excludedStatusList(filterModeParts)}))` : raw("TRUE");
  const passingNovelties = sql`
    SELECT ${rankedNoveltyColumns(tables)}
    FROM ${tableRef(tables.userCustomListNoveltyItem)} m
    INNER JOIN ${tableRef(tables.novelty)} n ON m."novelty" = n."id"
    LEFT JOIN ${tableRef(tables.userReleaseEntryStatus)} urs
      ON urs."entry" = n."entryId"
     AND ${args.entryUserMatch}
    ${latestReadJoin(tables)}
    WHERE m."list" = ${param(listId)}
      AND ${args.listUserMatch}
      AND ${where}
      AND ${excludedSql}
      AND ${collectingWindowSql}
      AND ${modeSql2}
      AND ${altSql}
  `;
  return oneNoveltyPerBox({ passingNovelties, tables, orderBy, take, skip });
}
function sharedBoxKey() {
  return sql`CASE
      WHEN ${castText(raw('n."relationType"'))} = 'collection' AND COALESCE(n."isbn", '') <> ''
        THEN 'isbn:' || n."isbn"
      ELSE 'novelty:' || n."id"
    END`;
}
function isCanonicalTwin(tables) {
  if (tables.sharedReleaseCollection === void 0) return raw("FALSE");
  return sql`EXISTS (
      SELECT 1
      FROM ${tableRef(tables.sharedReleaseCollection)} box
      WHERE box."canonicalCollection" = n."relationId"
    )`;
}
function rankedNoveltyColumns(tables) {
  return sql`n."id" AS "id",
      ROW_NUMBER() OVER (
        PARTITION BY ${sharedBoxKey()}
        ORDER BY CASE WHEN ${isCanonicalTwin(tables)} THEN 0 ELSE 1 END, n."id"
      ) AS "boxRank"`;
}
function oneNoveltyPerBox(args) {
  const { passingNovelties, tables, orderBy, take, skip } = args;
  return sql`
    SELECT n."id"
    FROM (${passingNovelties}) passing
    INNER JOIN ${tableRef(tables.novelty)} n ON n."id" = passing."id"
    WHERE passing."boxRank" = 1
    ORDER BY ${orderBy}
    LIMIT ${limitSql(take)} OFFSET ${param(skip)}
  `;
}
function latestReadJoin(tables) {
  return dialectNode(
    (dialect) => isPg(dialect.name) ? sql`LEFT JOIN LATERAL (
          SELECT urrs."readStatus"
          FROM ${tableRef(tables.userReleaseReadStatus)} urrs
          WHERE urrs."entry" = urs."id"
          ORDER BY urrs."lastReadChanged" DESC NULLS LAST
          LIMIT 1
        ) latest_read ON TRUE` : sql`LEFT JOIN ${tableRef(tables.userReleaseReadStatus)} latest_read
          ON latest_read."entry" = urs."id"`
  );
}

// src/collectionFilterMode.ts
var isPg2 = (name) => name === "postgres";
function tableRef2(name) {
  return raw(`"${name}"`);
}
var RELEASED_URS = raw(
  `(COALESCE(urs."totalVolumes", 0) - COALESCE(urs."totalNotReleasedVolumes", 0))`
);
var RELEASED_RE = raw(
  `(COALESCE(re."totalVolumes", 0) - COALESCE(re."totalNotReleasedVolumes", 0))`
);
function distinctActiveBaseCount(mapExpr, activePg, activeSqlite) {
  return dialectNode(
    (dialect) => isPg2(dialect.name) ? raw(
      `(SELECT COUNT(DISTINCT split_part(kv.key, '-', 1)) FROM jsonb_each_text(${mapExpr}) AS kv(key, value) WHERE ${activePg})`
    ) : raw(
      `(SELECT COUNT(DISTINCT (CASE WHEN instr(kv.key, '-') > 0 THEN substr(kv.key, 1, instr(kv.key, '-') - 1) ELSE kv.key END)) FROM json_each(${mapExpr}) AS kv WHERE ${activeSqlite})`
    )
  );
}
function allOwnedRead() {
  return dialectNode(
    (dialect) => isPg2(dialect.name) ? raw(`NOT EXISTS (
          SELECT 1 FROM jsonb_each_text(urs."itemStatus") AS o(key, value)
          WHERE COALESCE(o.value::int, 0) > 0
            AND NOT EXISTS (
              SELECT 1 FROM jsonb_each_text(latest_read."readStatus") AS r(key, value)
              WHERE r.value = 'true'
                AND split_part(r.key, '-', 1) = split_part(o.key, '-', 1)
            )
        )`) : raw(`NOT EXISTS (
          SELECT 1 FROM json_each(urs."itemStatus") AS o
          WHERE COALESCE(CAST(o.value AS INTEGER), 0) > 0
            AND NOT EXISTS (
              SELECT 1 FROM json_each(latest_read."readStatus") AS r
              WHERE r.value IS 1
                AND (CASE WHEN instr(r.key, '-') > 0 THEN substr(r.key, 1, instr(r.key, '-') - 1) ELSE r.key END)
                  = (CASE WHEN instr(o.key, '-') > 0 THEN substr(o.key, 1, instr(o.key, '-') - 1) ELSE o.key END)
            )
        )`)
  );
}
function gapsSql(mapExpr, activePg, activeSqlite) {
  return dialectNode(
    (dialect) => isPg2(dialect.name) ? raw(`(
          SELECT COALESCE(MIN(g.base) FILTER (WHERE NOT g.act), 'Infinity'::numeric)
               < COALESCE(MAX(g.base) FILTER (WHERE g.act), '-Infinity'::numeric)
          FROM (
            SELECT split_part(kv.key, '-', 1)::numeric AS base, bool_or(${activePg}) AS act
            FROM jsonb_each_text(${mapExpr}) AS kv(key, value)
            GROUP BY split_part(kv.key, '-', 1)
          ) g
        )`) : raw(`(
          SELECT COALESCE(MIN(CASE WHEN g.act = 0 THEN g.base END), 1e18)
               < COALESCE(MAX(CASE WHEN g.act = 1 THEN g.base END), -1e18)
          FROM (
            SELECT CAST((CASE WHEN instr(kv.key, '-') > 0 THEN substr(kv.key, 1, instr(kv.key, '-') - 1) ELSE kv.key END) AS REAL) AS base,
                   MAX(CASE WHEN ${activeSqlite} THEN 1 ELSE 0 END) AS act
            FROM json_each(${mapExpr}) AS kv
            GROUP BY (CASE WHEN instr(kv.key, '-') > 0 THEN substr(kv.key, 1, instr(kv.key, '-') - 1) ELSE kv.key END)
          ) g
        )`)
  );
}
var COLLECTION_ACTIVE_PG = "COALESCE(kv.value::int, 0) > 0";
var COLLECTION_ACTIVE_SQLITE = "COALESCE(CAST(kv.value AS INTEGER), 0) > 0";
var READ_ACTIVE_PG = "kv.value = 'true'";
var READ_ACTIVE_SQLITE = "kv.value IS 1";
var ownedBaseCount = distinctActiveBaseCount(
  'urs."itemStatus"',
  COLLECTION_ACTIVE_PG,
  COLLECTION_ACTIVE_SQLITE
);
var readBaseCount = distinctActiveBaseCount(
  'latest_read."readStatus"',
  READ_ACTIVE_PG,
  READ_ACTIVE_SQLITE
);
function collectionComplete(released) {
  return sql`(${released} <= 0 OR ${ownedBaseCount} >= ${released})`;
}
function readComplete(released) {
  return sql`(
    ${released} <= 0
    OR (
      latest_read."readStatus" IS NOT NULL
      AND (
        CASE
          WHEN ${ownedBaseCount} > ${released} THEN ${allOwnedRead()}
          ELSE ${readBaseCount} >= ${released}
        END
      )
    )
  )`;
}
function modeSql(filterMode, context, released) {
  if (filterMode === "allDone") {
    return context === "read" ? readComplete(released) : collectionComplete(released);
  }
  if (filterMode === "missing") {
    return sql`(NOT ${context === "read" ? readComplete(released) : collectionComplete(released)})`;
  }
  if (filterMode === "gaps") {
    return context === "read" ? gapsSql('latest_read."readStatus"', READ_ACTIVE_PG, READ_ACTIVE_SQLITE) : gapsSql('urs."itemStatus"', COLLECTION_ACTIVE_PG, COLLECTION_ACTIVE_SQLITE);
  }
  if (filterMode === "unowned") return sql`(${ownedBaseCount} = 0)`;
  if (filterMode === "unread") return sql`(NOT ${readComplete(released)})`;
  return raw("TRUE");
}
var COLLECTION_FILTER_MODES = ["allDone", "missing", "gaps", "unowned", "unread"];
function collectionModePredicate(filterMode, context, released) {
  if (!filterMode || !COLLECTION_FILTER_MODES.includes(filterMode)) return raw("TRUE");
  return modeSql(filterMode, context, released);
}
function latestReadJoin2(tables) {
  return dialectNode(
    (dialect) => isPg2(dialect.name) ? sql`LEFT JOIN LATERAL (
          SELECT urrs."readStatus"
          FROM ${tableRef2(tables.userReleaseReadStatus)} urrs
          WHERE urrs."entry" = urs."id"
          ORDER BY urrs."lastReadChanged" DESC NULLS LAST
          LIMIT 1
        ) latest_read ON TRUE` : sql`LEFT JOIN (
          SELECT urrs."entry", urrs."readStatus",
                 ROW_NUMBER() OVER (
                   PARTITION BY urrs."entry"
                   ORDER BY urrs."lastReadChanged" DESC NULLS LAST
                 ) AS "readRank"
          FROM ${tableRef2(tables.userReleaseReadStatus)} urrs
        ) latest_read
          ON latest_read."entry" = urs."id" AND latest_read."readRank" = 1`
  );
}

// src/filteredUserReleaseEntryStatus.ts
var isPg3 = (name) => name === "postgres";
function tableRef3(name) {
  return raw(`"${name}"`);
}
function limitSql2(take) {
  if (take === void 0 || take === null) {
    return dialectNode((dialect) => isPg3(dialect.name) ? raw("ALL") : raw("-1"));
  }
  return param(take);
}
function buildFilteredUserReleaseEntryStatusIdsSql(args) {
  const { where, orderBy, filterMode, context, take, skip, tables } = args;
  const mode = collectionModePredicate(filterMode, context, RELEASED_URS);
  return sql`
    SELECT urs."id"
    FROM ${tableRef3(tables.userReleaseEntryStatus)} urs
    ${latestReadJoin2(tables)}
    WHERE ${where}
      AND ${args.userMatch}
      AND ${mode}
    ORDER BY ${orderBy}
    LIMIT ${limitSql2(take)} OFFSET ${param(skip)}
  `;
}
function buildFilteredCustomListReleaseEntryIdsSql(args) {
  const { listId, where, orderBy, filterMode, take, skip, tables } = args;
  const mode = collectionModePredicate(filterMode, "collection", RELEASED_RE);
  return sql`
    SELECT re."id"
    FROM ${tableRef3(tables.userCustomListReleaseEntryItem)} m
    INNER JOIN ${tableRef3(tables.releaseEntry)} re ON m."releaseEntry" = re."id"
    LEFT JOIN ${tableRef3(tables.userReleaseEntryStatus)} urs
      ON urs."entry" = re."id"
     AND ${args.entryUserMatch}
    ${latestReadJoin2(tables)}
    WHERE m."list" = ${param(listId)}
      AND ${args.listUserMatch}
      AND ${where}
      AND ${mode}
    ORDER BY ${orderBy}
    LIMIT ${limitSql2(take)} OFFSET ${param(skip)}
  `;
}

// src/databaseListReleaseEntries.ts
var isPg4 = (name) => name === "postgres";
function tableRef4(name) {
  return raw(`"${name}"`);
}
function limitSql3(take) {
  if (take === void 0 || take === null) {
    return dialectNode((dialect) => isPg4(dialect.name) ? raw("ALL") : raw("-1"));
  }
  return param(take);
}
function buildDatabaseListReleaseEntryIdsSql(args) {
  const { databaseListId, where, direction, take, skip, tables } = args;
  const order = raw(direction === "desc" ? "DESC" : "ASC");
  return sql`
    SELECT m."id"
    FROM ${tableRef4(tables.databaseListReleaseEntry)} m
    INNER JOIN ${tableRef4(tables.releaseEntry)} re ON m."releaseEntry" = re."id"
    WHERE m."databaseList" = ${param(databaseListId)}
      AND ${where}
    ORDER BY m."orderNumber" ${order}, m."id" ASC
    LIMIT ${limitSql3(take)} OFFSET ${param(skip)}
  `;
}

// src/userCustomListStats.ts
function tableRef5(name) {
  return raw(`"${name}"`);
}
function buildUserCustomListStatsSql(args) {
  if (args.targetType === "novelty") {
    return sql`
      SELECT
        COUNT(*) AS "itemCount",
        0 AS "seriesCount",
        0 AS "volumeCount",
        COALESCE(SUM(${args.noveltyPrice}), 0) AS "totalValue"
      FROM ${tableRef5(args.tables.membership)} m
      INNER JOIN ${tableRef5(args.tables.novelty)} n ON m."novelty" = n."id"
      WHERE m."list" = ${param(args.listId)}
        AND ${args.listUserMatch}
    `;
  }
  const volumeCount = dialectNode(
    (dialect) => sql`COALESCE(${dialect.jsonArrayLength(args.sortedVolumeNumbers)}, 0)`
  );
  const itemsPrice = sql`(
    SELECT COALESCE(SUM(${args.releaseItemPrice}), 0)
    FROM ${tableRef5(args.tables.releaseItem)} ri
    WHERE ${args.releaseItemEntryId} = re."id"
  )`;
  const collectionsPrice = sql`(
    SELECT COALESCE(SUM(${args.releaseItemCollectionPrice}), 0)
    FROM ${tableRef5(args.tables.releaseItemCollection)} rc
    WHERE ${args.releaseItemCollectionEntryId} = re."id"
  )`;
  return sql`
    SELECT
      COUNT(*) AS "itemCount",
      COUNT(*) AS "seriesCount",
      COALESCE(SUM(${volumeCount}), 0) AS "volumeCount",
      COALESCE(SUM(${itemsPrice} + ${collectionsPrice}), 0) AS "totalValue"
    FROM ${tableRef5(args.tables.membership)} m
    INNER JOIN ${tableRef5(args.tables.releaseEntry)} re ON m."releaseEntry" = re."id"
    WHERE m."list" = ${param(args.listId)}
      AND ${args.listUserMatch}
  `;
}

// src/volumeKey.ts
var VOLUME_KEY_DELIMITER = "-";
function volumeKeyNumber(key) {
  return dialectNode(
    (dialect) => dialect.castNumeric(dialect.substringBefore(key, VOLUME_KEY_DELIMITER))
  );
}
function volumeKeySuffix(key) {
  return dialectNode(
    (dialect) => sql`COALESCE(${dialect.substringAfter(key, VOLUME_KEY_DELIMITER)}, '')`
  );
}

// src/userCollectionBreakdown.ts
var DIMENSIONS = ["total", "publisher", "type", "releaseYear"];
var MEASURES = ["series", "volumes", "value", "readSeries", "readVolumes"];
var SOURCES = [
  {
    cte: "owned_volume",
    alias: "ov",
    releaseYearExpr: 'ov."volumeYear"',
    measureExprs: {
      volumes: 'COALESCE(SUM(ov."quantity"), 0)',
      value: 'COALESCE(SUM(ov."value"), 0)'
    }
  },
  {
    cte: "owned_series",
    alias: "osr",
    releaseYearExpr: 'osr."entryYear"',
    measureExprs: { series: "COUNT(*)" }
  },
  {
    cte: "read_volume",
    alias: "rv",
    releaseYearExpr: 'rv."volumeYear"',
    measureExprs: { readVolumes: "COUNT(*)" }
  },
  {
    cte: "read_series",
    alias: "rs",
    releaseYearExpr: 'rs."entryYear"',
    measureExprs: { readSeries: "COUNT(*)" }
  }
];
function tableRef6(name) {
  return raw(`"${name}"`);
}
function textLiteral(value) {
  return dialectNode((dialect) => dialect.castText(raw(`'${value}'`)));
}
function releaseYearBucket(source) {
  return dialectNode(
    (dialect) => dialect.castText(raw(`COALESCE(${source.releaseYearExpr}, -1)`))
  );
}
function bucketExpr(source, dimension) {
  if (dimension === "total") return textLiteral("");
  if (dimension === "publisher") return raw(`${source.alias}."publisher"`);
  if (dimension === "type") return raw(`${source.alias}."type"`);
  return releaseYearBucket(source);
}
function branch(source, dimension) {
  const bucket = bucketExpr(source, dimension);
  const measures = MEASURES.map(
    (measure) => sql`${raw(source.measureExprs[measure] ?? "0")} AS ${raw(`"${measure}"`)}`
  );
  const grouping = dimension === "total" ? raw("") : sql` GROUP BY ${bucket}`;
  return sql`SELECT ${textLiteral(dimension)} AS "dimension", ${bucket} AS "bucket", ${join(measures, ", ")} FROM ${raw(source.cte)} ${raw(source.alias)}${grouping}`;
}
function ownedVolumeCte(args) {
  return dialectNode((dialect) => {
    const quantity = dialect.castInt(raw("o.value"));
    return sql`owned_volume AS (
      SELECT
        COALESCE(${args.statusPublisherName}, '') AS "publisher",
        COALESCE(${args.statusType}, '') AS "type",
        ${args.itemReleaseYear} AS "volumeYear",
        ${quantity} AS "quantity",
        ${quantity} * COALESCE(${args.itemPrice}, 0) AS "value"
      FROM ${tableRef6(args.tables.userReleaseEntryStatus)} urs
      CROSS JOIN ${dialect.jsonEachText(raw('urs."itemStatus"'), "o")}
      LEFT JOIN ${tableRef6(args.tables.releaseItem)} ri
        ON ${args.itemEntryId} = urs."entry"
       AND ${args.itemNumber} = ${volumeKeyNumber(raw("o.key"))}
       AND COALESCE(TRIM(${args.itemVolumeSuffix}), '') = ${volumeKeySuffix(raw("o.key"))}
      WHERE ${args.userMatch}
        AND urs."collectionStatus" <> 'sold'
        AND ${quantity} > 0
    )`;
  });
}
function ownedSeriesCte(args) {
  return dialectNode(
    (dialect) => sql`owned_series AS (
      SELECT
        COALESCE(${args.statusPublisherName}, '') AS "publisher",
        COALESCE(${args.statusType}, '') AS "type",
        ${args.entryReleaseYear} AS "entryYear"
      FROM ${tableRef6(args.tables.userReleaseEntryStatus)} urs
      LEFT JOIN ${tableRef6(args.tables.releaseEntry)} re ON re."id" = urs."entry"
      WHERE ${args.userMatch}
        AND urs."collectionStatus" <> 'sold'
        AND EXISTS (
          SELECT 1 FROM ${dialect.jsonEachText(raw('urs."itemStatus"'), "oe")}
          WHERE ${dialect.castInt(raw("oe.value"))} > 0
        )
    )`
  );
}
function readVolumeCte(args) {
  return dialectNode(
    (dialect) => sql`read_volume AS (
      SELECT
        urs."id" AS "statusId",
        COALESCE(${args.statusPublisherName}, '') AS "publisher",
        COALESCE(${args.statusType}, '') AS "type",
        ${args.entryReleaseYear} AS "entryYear",
        ${args.itemReleaseYear} AS "volumeYear"
      FROM ${tableRef6(args.tables.userReleaseEntryStatus)} urs
      JOIN ${tableRef6(args.tables.userReleaseReadStatus)} urrs ON urrs."entry" = urs."id"
      CROSS JOIN ${dialect.jsonEachText(raw('urrs."readStatus"'), "r")}
      LEFT JOIN ${tableRef6(args.tables.releaseEntry)} re ON re."id" = urs."entry"
      LEFT JOIN ${tableRef6(args.tables.releaseItem)} ri
        ON ${args.itemEntryId} = urs."entry"
       AND ${args.itemNumber} = ${volumeKeyNumber(raw("r.key"))}
       AND COALESCE(TRIM(${args.itemVolumeSuffix}), '') = ${volumeKeySuffix(raw("r.key"))}
      WHERE ${args.userMatch}
        AND ${dialect.jsonValueIsTrue(raw("r.value"))}
    )`
  );
}
function buildUserCollectionBreakdownSql(args) {
  const branches = SOURCES.flatMap(
    (source) => DIMENSIONS.map((dimension) => branch(source, dimension))
  );
  const sumMeasures = MEASURES.map(
    (measure) => sql`COALESCE(SUM(f.${raw(`"${measure}"`)}), 0) AS ${raw(`"${measure}"`)}`
  );
  return sql`
    WITH ${ownedVolumeCte(args)},
    ${ownedSeriesCte(args)},
    ${readVolumeCte(args)},
    read_series AS (
      SELECT DISTINCT "statusId", "publisher", "type", "entryYear" FROM read_volume
    )
    SELECT f."dimension", f."bucket", ${join(sumMeasures, ", ")}
    FROM (
      ${join(branches, "\n      UNION ALL\n      ")}
    ) f
    GROUP BY f."dimension", f."bucket"
  `;
}

// src/readRecommendations.ts
var isPg5 = (name) => name === "postgres";
var EXCLUDED_COLLECTION_STATUSES = ["sold", "aborted", "planned"];
var FREE_READ_RECOMMENDATION_OPTIONS = {
  amount: 1,
  selectionMode: "single",
  ignoreOwned: false
};
var MAX_READ_RECOMMENDATION_AMOUNT = 20;
function resolveReadRecommendationOptions(request, isPremium) {
  if (!isPremium) return { ...FREE_READ_RECOMMENDATION_OPTIONS };
  const requestedAmount = request.amount ?? FREE_READ_RECOMMENDATION_OPTIONS.amount;
  return {
    amount: clampAmount(requestedAmount),
    selectionMode: request.selectionMode ?? FREE_READ_RECOMMENDATION_OPTIONS.selectionMode,
    ignoreOwned: request.ignoreOwned ?? FREE_READ_RECOMMENDATION_OPTIONS.ignoreOwned
  };
}
function clampAmount(amount) {
  if (!Number.isFinite(amount)) return FREE_READ_RECOMMENDATION_OPTIONS.amount;
  const whole = Math.trunc(amount);
  if (whole < 1) return 1;
  if (whole > MAX_READ_RECOMMENDATION_AMOUNT) return MAX_READ_RECOMMENDATION_AMOUNT;
  return whole;
}
function tableRef7(name) {
  return raw(`"${name}"`);
}
function baseNumber(keyExpr) {
  return dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`split_part(${keyExpr}, '-', 1)` : sql`(CASE WHEN instr(${keyExpr}, '-') > 0 THEN substr(${keyExpr}, 1, instr(${keyExpr}, '-') - 1) ELSE ${keyExpr} END)`
  );
}
function castNumeric(expr) {
  return dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`(${expr})::numeric` : sql`CAST(${expr} AS REAL)`
  );
}
function defaultRandomExpr() {
  return raw("random()");
}
function baseNotRead(base) {
  return dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`NOT EXISTS (
          SELECT 1
          FROM jsonb_each_text(latest_read."readStatus") AS r(key, value)
          WHERE r.value = 'true'
            AND split_part(r.key, '-', 1) = ${base}
        )` : sql`NOT EXISTS (
          SELECT 1
          FROM json_each(latest_read."readStatus") AS r
          WHERE r.value IS 1
            AND ${baseNumber(raw("r.key"))} = ${base}
        )`
  );
}
function membershipPredicate(membership) {
  if (membership?.targetType !== "releaseEntry") return raw("TRUE");
  return sql`EXISTS (
    SELECT 1
    FROM ${tableRef7(membership.table)} m
    WHERE m."list" = ${param(membership.listId)}
      AND ${membership.listUserMatch}
      AND m."releaseEntry" = urs."entry"
  )`;
}
function scopePredicate(scope) {
  if (!scope) return raw("TRUE");
  const parts = [
    scope.where ?? raw("TRUE"),
    collectionModePredicate(scope.filterMode, scope.filterContext ?? "collection", RELEASED_URS),
    membershipPredicate(scope.membership)
  ];
  return sql`(${join(parts, " AND ")})`;
}
function collectedSeriesPredicate(args) {
  const collectionStatusText = dialectNode(
    (dialect) => isPg5(dialect.name) ? raw('urs."collectionStatus"::text') : raw('CAST(urs."collectionStatus" AS TEXT)')
  );
  const excluded = join(
    EXCLUDED_COLLECTION_STATUSES.map((status) => param(status)),
    ", "
  );
  return sql`${args.userMatch}
    AND ${collectionStatusText} NOT IN (${excluded})
    AND ${scopePredicate(args.scope)}`;
}
function ownedCandidates(args) {
  const base = baseNumber(raw("kv.key"));
  const itemStatusJoin = dialectNode(
    (dialect) => isPg5(dialect.name) ? raw(`CROSS JOIN LATERAL jsonb_each_text(urs."itemStatus") AS kv(key, value)`) : raw(`CROSS JOIN json_each(urs."itemStatus") AS kv`)
  );
  const ownedQuantity = dialectNode(
    (dialect) => isPg5(dialect.name) ? raw("COALESCE(kv.value::int, 0) > 0") : raw("COALESCE(CAST(kv.value AS INTEGER), 0) > 0")
  );
  return sql`
    SELECT
      urs."id" AS "statusId",
      urs."entry" AS "entryId",
      ${base} AS "volumeNumber"
    FROM ${tableRef7(args.tables.userReleaseEntryStatus)} urs
    ${itemStatusJoin}
    ${latestReadJoin2(args.tables)}
    WHERE ${collectedSeriesPredicate(args)}
      AND ${ownedQuantity}
      AND ${baseNotRead(base)}
  `;
}
function releasedUnownedCandidates(args) {
  const releaseEntryTable = args.tables.releaseEntry;
  const { sortedVolumeNumbers, notReleasedVolumeNumbers } = args;
  if (!releaseEntryTable || !sortedVolumeNumbers || !notReleasedVolumeNumbers) {
    throw new Error(
      "readRecommendations: tables.releaseEntry, sortedVolumeNumbers and notReleasedVolumeNumbers are required when ignoreOwned is true"
    );
  }
  const base = baseNumber(raw("kv.value"));
  const volumeNumbersJoin = dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`CROSS JOIN LATERAL jsonb_array_elements_text(${sortedVolumeNumbers}) AS kv(value)` : sql`CROSS JOIN json_each(${sortedVolumeNumbers}) AS kv`
  );
  const isReleased = dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`NOT EXISTS (
          SELECT 1
          FROM jsonb_array_elements_text(${notReleasedVolumeNumbers}) AS nr(value)
          WHERE nr.value = kv.value
        )` : sql`NOT EXISTS (
          SELECT 1
          FROM json_each(${notReleasedVolumeNumbers}) AS nr
          WHERE nr.value = kv.value
        )`
  );
  return sql`
    SELECT
      urs."id" AS "statusId",
      urs."entry" AS "entryId",
      ${base} AS "volumeNumber"
    FROM ${tableRef7(args.tables.userReleaseEntryStatus)} urs
    INNER JOIN ${tableRef7(releaseEntryTable)} re ON re."id" = urs."entry"
    ${volumeNumbersJoin}
    ${latestReadJoin2(args.tables)}
    WHERE ${collectedSeriesPredicate(args)}
      AND ${isReleased}
      AND ${baseNotRead(base)}
  `;
}
function candidatesCte(args) {
  if (!args.ignoreOwned) return sql`SELECT DISTINCT * FROM (${ownedCandidates(args)}) owned`;
  return sql`${ownedCandidates(args)} UNION ${releasedUnownedCandidates(args)}`;
}
function listedVolumeMembershipOf(scope) {
  const membership = scope?.membership;
  return membership?.targetType === "novelty" ? membership : null;
}
function volumeNumberWithoutTrailingZeroFraction(numberExpr) {
  const asText = dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`(${numberExpr})::text` : sql`CAST(${numberExpr} AS TEXT)`
  );
  return sql`(CASE WHEN ${asText} LIKE '%.0' THEN substr(${asText}, 1, length(${asText}) - 2) ELSE ${asText} END)`;
}
function listedVolumeNotRead(releaseItemNumber) {
  const readValue = dialectNode(
    (dialect) => isPg5(dialect.name) ? sql`jsonb_each_text(latest_read."readStatus") AS r(key, value)` : sql`json_each(latest_read."readStatus") AS r`
  );
  const isReadValue = dialectNode(
    (dialect) => isPg5(dialect.name) ? raw(`r.value = 'true'`) : raw("r.value IS 1")
  );
  return sql`NOT EXISTS (
    SELECT 1
    FROM ${readValue}
    WHERE ${isReadValue}
      AND ${volumeKeyNumber(raw("r.key"))} = ${castNumeric(releaseItemNumber)}
  )`;
}
function unreadListedVolumeCandidates(membership, readContext) {
  return sql`
    SELECT DISTINCT
      ${membership.noveltyEntryId} AS "statusId",
      ${membership.noveltyEntryId} AS "entryId",
      ${volumeNumberWithoutTrailingZeroFraction(membership.releaseItemNumber)} AS "volumeNumber",
      ri."id" AS "itemId"
    FROM ${tableRef7(membership.table)} m
    INNER JOIN ${tableRef7(membership.noveltyTable)} n ON n."id" = m."novelty"
    INNER JOIN ${tableRef7(membership.releaseItemTable)} ri ON ri."id" = ${membership.noveltyRelationId}
    LEFT JOIN ${tableRef7(readContext.tables.userReleaseEntryStatus)} urs
      ON urs."entry" = ${membership.noveltyEntryId} AND ${readContext.userMatch}
    ${latestReadJoin2(readContext.tables)}
    WHERE m."list" = ${param(membership.listId)}
      AND ${membership.listUserMatch}
      AND ${membership.noveltyRelationType} = ${param("volume")}
      AND ${listedVolumeNotRead(membership.releaseItemNumber)}
  `;
}
function nextUnreadListedVolumeCandidates(membership, readContext) {
  const volumeOrder = castNumeric(raw('"volumeNumber"'));
  return sql`
    SELECT "statusId", "entryId", "volumeNumber", "itemId"
    FROM (
      SELECT
        "statusId",
        "entryId",
        "volumeNumber",
        "itemId",
        ${volumeOrder} AS "volumeOrder",
        MIN(${volumeOrder}) OVER (PARTITION BY "statusId") AS "lowestVolumeOrder"
      FROM (${unreadListedVolumeCandidates(membership, readContext)}) unread
    ) ranked
    WHERE "volumeOrder" = "lowestVolumeOrder"
  `;
}
function buildListedVolumeDrawSql(args, membership) {
  const randomExpr = args.randomExpr ?? defaultRandomExpr();
  if (args.selectionMode === "single") {
    return sql`
      WITH "candidates" AS (${nextUnreadListedVolumeCandidates(membership, args)})
      SELECT "statusId", "entryId", "volumeNumber", "itemId"
      FROM "candidates"
      ORDER BY ${randomExpr} ASC
      LIMIT ${param(args.amount)}
    `;
  }
  return sql`
    WITH "candidates" AS (${unreadListedVolumeCandidates(membership, args)}),
    "pickedSeries" AS (
      SELECT "statusId", ROW_NUMBER() OVER (ORDER BY "seriesRandom") AS "seriesRank"
      FROM (
        SELECT "statusId", MIN(${randomExpr}) AS "seriesRandom"
        FROM "candidates"
        GROUP BY "statusId"
      ) drawn
    )
    SELECT c."statusId", c."entryId", c."volumeNumber", c."itemId"
    FROM "candidates" c
    INNER JOIN "pickedSeries" p ON p."statusId" = c."statusId"
    WHERE p."seriesRank" <= ${param(args.amount)}
    ORDER BY p."seriesRank" ASC, ${castNumeric(raw('c."volumeNumber"'))} ASC
  `;
}
function buildReadRecommendationsSql(args) {
  const listedVolumeMembership = listedVolumeMembershipOf(args.scope);
  if (listedVolumeMembership) return buildListedVolumeDrawSql(args, listedVolumeMembership);
  const randomExpr = args.randomExpr ?? defaultRandomExpr();
  const candidates = candidatesCte(args);
  if (args.selectionMode === "single") {
    return sql`
      WITH "candidates" AS (${candidates}),
      "drawn" AS (
        SELECT "statusId", "entryId", "volumeNumber", ${randomExpr} AS "volumeDraw"
        FROM "candidates"
      ),
      "runOrdered" AS (
        SELECT
          "statusId",
          "entryId",
          "volumeNumber",
          MAX("volumeDraw") OVER (
            PARTITION BY "statusId"
            ORDER BY ${castNumeric(raw('"volumeNumber"'))} ASC
            ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW
          ) AS "runDraw"
        FROM "drawn"
      )
      SELECT "statusId", "entryId", "volumeNumber"
      FROM "runOrdered"
      ORDER BY "runDraw" ASC, ${castNumeric(raw('"volumeNumber"'))} ASC
      LIMIT ${param(args.amount)}
    `;
  }
  return sql`
    WITH "candidates" AS (${candidates}),
    "pickedSeries" AS (
      SELECT "statusId", ROW_NUMBER() OVER (ORDER BY "seriesRandom") AS "seriesRank"
      FROM (
        SELECT "statusId", MIN(${randomExpr}) AS "seriesRandom"
        FROM "candidates"
        GROUP BY "statusId"
      ) drawn
    )
    SELECT c."statusId", c."entryId", c."volumeNumber"
    FROM "candidates" c
    INNER JOIN "pickedSeries" p ON p."statusId" = c."statusId"
    WHERE p."seriesRank" <= ${param(args.amount)}
    ORDER BY p."seriesRank" ASC, ${castNumeric(raw('c."volumeNumber"'))} ASC
  `;
}
function groupReadRecommendations(rows, selectionMode) {
  const recommendations = [];
  const bySeries = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const existing = selectionMode === "series" ? bySeries.get(row.statusId) : void 0;
    if (existing) {
      existing.volumeNumbers.push(row.volumeNumber);
      if (row.itemId) existing.itemIds.push(row.itemId);
      continue;
    }
    const recommendation = {
      statusId: row.statusId,
      entryId: row.entryId,
      volumeNumbers: [row.volumeNumber],
      itemIds: row.itemId ? [row.itemId] : []
    };
    bySeries.set(row.statusId, recommendation);
    recommendations.push(recommendation);
  }
  return recommendations;
}

// src/profileStatistics.ts
function tableRef8(name) {
  return raw(`"${name}"`);
}
function distinctOwnedVolumeNumbersCte(args) {
  return dialectNode(
    (dialect) => sql`owned_volume_number AS (
      SELECT DISTINCT urs."id" AS "statusId", ${volumeKeyNumber(raw("o.key"))} AS "volumeNumber"
      FROM ${tableRef8(args.tables.userReleaseEntryStatus)} urs
      CROSS JOIN ${dialect.jsonEachText(raw('urs."itemStatus"'), "o")}
      WHERE ${args.userMatch}
        AND urs."collectionStatus" <> 'sold'
        AND ${dialect.castInt(raw("o.value"))} > 0
    )`
  );
}
function ownedVolumeNumbersReadInLatestPassCte(args) {
  return dialectNode(
    (dialect) => sql`owned_volume_number_read_in_latest_pass AS (
      SELECT DISTINCT urs."id" AS "statusId", ${volumeKeyNumber(raw("o.key"))} AS "volumeNumber"
      FROM ${tableRef8(args.tables.userReleaseEntryStatus)} urs
      CROSS JOIN ${dialect.jsonEachText(raw('urs."itemStatus"'), "o")}
      ${latestReadJoin2({ userReleaseReadStatus: args.tables.userReleaseReadStatus })}
      CROSS JOIN ${dialect.jsonEachText(raw('latest_read."readStatus"'), "r")}
      WHERE ${args.userMatch}
        AND urs."collectionStatus" <> 'sold'
        AND ${dialect.castInt(raw("o.value"))} > 0
        AND r.key = o.key
        AND ${dialect.jsonValueIsTrue(raw("r.value"))}
    )`
  );
}
function buildProfileStatisticsSql(args) {
  return sql`
    WITH ${ownedVolumeCte(args)},
    ${ownedSeriesCte(args)},
    ${distinctOwnedVolumeNumbersCte(args)},
    ${ownedVolumeNumbersReadInLatestPassCte(args)}
    SELECT
      (SELECT COUNT(*) FROM owned_series) AS "series",
      (SELECT COALESCE(SUM("quantity"), 0) FROM owned_volume) AS "volumes",
      (SELECT COUNT(*) FROM owned_volume_number) AS "ownedVolumeNumbers",
      (SELECT COUNT(*) FROM owned_volume_number_read_in_latest_pass) AS "ownedVolumeNumbersReadInLatestPass"
  `;
}
function percentOfOwnedVolumeNumbersRead(ownedVolumeNumbersRead, ownedVolumeNumbers) {
  if (ownedVolumeNumbers === 0) return 0;
  return Math.round(ownedVolumeNumbersRead / ownedVolumeNumbers * 100);
}
function profileStatisticsFromRow(row) {
  return {
    series: row.series,
    volumes: row.volumes,
    readPercent: percentOfOwnedVolumeNumbersRead(
      row.ownedVolumeNumbersReadInLatestPass,
      row.ownedVolumeNumbers
    )
  };
}

// src/reelStripSample.ts
var REEL_STRIP_SAMPLE_SIZE = 16;
function tableRef9(name) {
  return raw(`"${name}"`);
}
function sampledInRandomOrder(rowsSql, randomExpr) {
  return sql`
    SELECT "statusId", "entryId", "volumeNumber", "itemId"
    FROM (${rowsSql}) sampled
    ORDER BY ${randomExpr} ASC
    LIMIT ${param(REEL_STRIP_SAMPLE_SIZE)}
  `;
}
function listedVolumeSampleSql(args, membership, randomExpr) {
  return sampledInRandomOrder(nextUnreadListedVolumeCandidates(membership, args), randomExpr);
}
function plainEditionFirst(releaseItem) {
  return sql`CASE WHEN COALESCE(${releaseItem.volumeSuffix}, '') = '' THEN 0 ELSE 1 END ASC, ri."id" ASC`;
}
function nextUnreadVolumeSampleSql(args, randomExpr) {
  const freeDraw = {
    ...FREE_READ_RECOMMENDATION_OPTIONS,
    tables: args.tables,
    userMatch: args.userMatch,
    scope: args.scope
  };
  const { releaseItem } = args;
  const nextUnreadEditions = sql`
    WITH "candidates" AS (${candidatesCte(freeDraw)}),
    "nextUnread" AS (
      SELECT
        "statusId",
        "entryId",
        "volumeNumber",
        ROW_NUMBER() OVER (
          PARTITION BY "statusId"
          ORDER BY ${castNumeric(raw('"volumeNumber"'))} ASC
        ) AS "seriesRank"
      FROM "candidates"
    ),
    "editions" AS (
      SELECT
        nu."statusId",
        nu."entryId",
        nu."volumeNumber",
        ri."id" AS "itemId",
        ROW_NUMBER() OVER (
          PARTITION BY nu."statusId"
          ORDER BY ${plainEditionFirst(releaseItem)}
        ) AS "editionRank"
      FROM "nextUnread" nu
      INNER JOIN ${tableRef9(releaseItem.table)} ri
        ON ${releaseItem.entryId} = nu."entryId"
       AND ${castNumeric(releaseItem.number)} = ${castNumeric(raw('nu."volumeNumber"'))}
      WHERE nu."seriesRank" = 1
    )
    SELECT "statusId", "entryId", "volumeNumber", "itemId"
    FROM "editions"
    WHERE "editionRank" = 1
  `;
  return sampledInRandomOrder(nextUnreadEditions, randomExpr);
}
function buildReelStripSampleSql(args) {
  const randomExpr = args.randomExpr ?? defaultRandomExpr();
  const listedVolumeMembership = listedVolumeMembershipOf(args.scope);
  if (listedVolumeMembership) return listedVolumeSampleSql(args, listedVolumeMembership, randomExpr);
  return nextUnreadVolumeSampleSql(args, randomExpr);
}
export {
  COLLECTION_FILTER_MODES,
  FREE_READ_RECOMMENDATION_OPTIONS,
  MAX_READ_RECOMMENDATION_AMOUNT,
  NOT_IN_COLLECTION_MODE,
  REEL_STRIP_SAMPLE_SIZE,
  RELEASED_RE,
  RELEASED_URS,
  buildCatalogueNoveltyIdsSql,
  buildDatabaseListReleaseEntryIdsSql,
  buildFilteredCustomListNoveltyIdsSql,
  buildFilteredCustomListReleaseEntryIdsSql,
  buildFilteredNoveltyIdsSql,
  buildFilteredUserReleaseEntryStatusIdsSql,
  buildProfileStatisticsSql,
  buildReadRecommendationsSql,
  buildReelStripSampleSql,
  buildUserCollectionBreakdownSql,
  buildUserCustomListStatsSql,
  candidatesCte,
  castNumeric,
  coerce,
  collectionModePredicate,
  createFilterBuilder,
  defaultRandomExpr,
  dialectNode,
  extractTargetMonth,
  groupReadRecommendations,
  join,
  latestReadJoin2 as latestReadJoin,
  listedVolumeMembershipOf,
  nextUnreadListedVolumeCandidates,
  ownedSeriesCte,
  ownedVolumeCte,
  param,
  percentOfOwnedVolumeNumbersRead,
  positionalSink,
  postgresDialect,
  profileStatisticsFromRow,
  raw,
  render,
  renderSqlite,
  resolveReadRecommendationOptions,
  sql,
  sqliteDialect,
  unreadListedVolumeCandidates,
  volumeKeyNumber,
  volumeKeySuffix
};
//# sourceMappingURL=index.js.map